import * as React from "react";
import {
  loadChatSession,
  newChatSession,
  saveChatSession,
  streamChat,
  type ChatSessionState,
} from "./chat";
import type { ChatMessage, ChatToolBlock } from "./types";

/**
 * Owns the right-rail chat panel. Mounted once near the root so messages
 * persist across route changes and the panel never unmounts.
 */

interface ChatCtx {
  open: boolean;
  toggle: () => void;
  setOpen: (v: boolean) => void;
  state: ChatSessionState;
  send: (text: string, options?: { append?: boolean }) => Promise<void>;
  cancel: () => void;
  streaming: boolean;
  newConversation: () => void;
  /** confirmation-pending state (server side requested user yes/no) */
  pendingConfirmation: { prompt: string; token?: string } | null;
  confirm: (yes: boolean) => Promise<void>;
}

const Ctx = React.createContext<ChatCtx | null>(null);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [state, setState] = React.useState<ChatSessionState>(() => loadChatSession());
  const [streaming, setStreaming] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = React.useState<
    { prompt: string; token?: string } | null
  >(null);

  // cmd-k toggles the panel — registered once.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // persist
  React.useEffect(() => {
    saveChatSession(state);
  }, [state]);

  const cancel = React.useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
  }, []);

  const newConversation = React.useCallback(() => {
    cancel();
    const fresh = newChatSession();
    setState(fresh);
    setPendingConfirmation(null);
  }, [cancel]);

  const runTurn = React.useCallback(async (msgs: ChatMessage[]) => {
    setStreaming(true);
    setPendingConfirmation(null);
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    // Push an empty assistant message and mutate it as tokens arrive.
    setState((cur) => ({
      ...cur,
      messages: [
        ...msgs,
        { role: "assistant", content: "", tool_blocks: [], streaming: true },
      ],
    }));

    const updateAssistant = (fn: (m: ChatMessage) => ChatMessage) =>
      setState((cur) => {
        const messages = cur.messages.slice();
        const i = messages.length - 1;
        if (i >= 0 && messages[i].role === "assistant") messages[i] = fn(messages[i]);
        return { ...cur, messages };
      });

    let sessionId = state.session_id;
    setState((cur) => {
      sessionId = cur.session_id;
      return cur;
    });

    try {
      await streamChat(
        { session_id: sessionId, messages: msgs, signal: ctrl.signal },
        {
          onToken: (delta) =>
            updateAssistant((m) => ({ ...m, content: (m.content ?? "") + delta })),
          onToolUse: (block) =>
            updateAssistant((m) => ({
              ...m,
              tool_blocks: [...(m.tool_blocks ?? []), block],
            })),
          onToolResult: (id, output, isError) =>
            updateAssistant((m) => ({
              ...m,
              tool_blocks: (m.tool_blocks ?? []).map((b: ChatToolBlock) =>
                b.id === id
                  ? { ...b, output, is_error: isError, state: isError ? "error" : "complete" }
                  : b,
              ),
            })),
          onConfirmRequest: (prompt, token) => {
            setPendingConfirmation({ prompt, token });
            updateAssistant((m) => ({
              ...m,
              pending_confirmation: { prompt, token },
            }));
          },
          onTurnEnd: ({ cost_cents }) =>
            updateAssistant((m) => ({ ...m, cost_cents, streaming: false })),
          onError: (msg) =>
            updateAssistant((m) => ({
              ...m,
              content: (m.content ?? "") + `\n\n[stream error: ${msg}]`,
              streaming: false,
            })),
        },
      );
    } finally {
      setStreaming(false);
      abortRef.current = null;
      updateAssistant((m) => ({ ...m, streaming: false }));
    }
  }, [state.session_id]);

  const send = React.useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || streaming) return;
      const userMsg: ChatMessage = { role: "user", content: trimmed };
      const next = [...state.messages, userMsg];
      setState((cur) => ({ ...cur, messages: next }));
      await runTurn(next);
    },
    [runTurn, state.messages, streaming],
  );

  const confirm = React.useCallback(
    async (yes: boolean) => {
      if (!pendingConfirmation) return;
      const reply = yes ? "yes, do it" : "no, hold off";
      setPendingConfirmation(null);
      await send(reply);
    },
    [pendingConfirmation, send],
  );

  const toggle = React.useCallback(() => setOpen((o) => !o), []);

  const value: ChatCtx = {
    open,
    toggle,
    setOpen,
    state,
    send,
    cancel,
    streaming,
    newConversation,
    pendingConfirmation,
    confirm,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useChat(): ChatCtx {
  const v = React.useContext(Ctx);
  if (!v) throw new Error("useChat must be used within ChatProvider");
  return v;
}
